import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  createAdminCustomRole,
  deleteAdminCustomRole,
  getAdminCustomRoles,
  updateAdminCustomRole,
  getRoleCatalogue,
  type AdminCustomRole,
  type AdminSummary,
  type RoleCatalogue,
  type RolePermissionGroup,
} from '../../../../api/adminApi';
import styles from './CustomRolesPanel.module.css';

type PermissionBucket = 'pages' | 'features' | 'actions';

type RoleDraft = {
  name: string;
  description: string;
  pages: string[];
  features: string[];
  actions: string[];
};

type PermissionOption = {
  key: string;
  label: string;
  description: string;
};

// The catalogue is fetched, not listed here. While it loads the editor shows
// nothing to tick rather than a stale subset, because a subset is what caused
// selections to disappear on save.
const EMPTY_CATALOGUE: RoleCatalogue = { pages: [], features: [], actions: [] };

const createEmptyDraft = (): RoleDraft => ({
  name: '',
  description: '',
  pages: ['overview', 'users'],
  features: ['ai_chat', 'reg_search'],
  actions: ['view_activity'],
});

function PermissionGroup({
  title,
  groups,
  selected,
  onToggle,
}: {
  title: string;
  groups: RolePermissionGroup[];
  selected: string[];
  onToggle: (key: string) => void;
}) {
  // Flattened only to count; the options render under their own sub-headings so
  // GRC reads as a set rather than five entries lost among sixteen.
  const options: PermissionOption[] = groups.flatMap((g) => g.items);
  return (
    <section className={styles.permissionGroup}>
      <div className={styles.groupHeader}>
        <span className={styles.groupLabel}>{title}</span>
        <span className={styles.countPill}>{selected.length} selected</span>
      </div>

      {options.length === 0 && (
        <p className={styles.optionDesc} data-testid="catalogue-empty">Loading what this role can be given…</p>
      )}

      {groups.map((group) => (
        <div key={group.group}>
          {groups.length > 1 && (
            <div className={styles.subGroupHeader} title={group.description}>
              {group.group}
              <span className={styles.subGroupCount}>
                {group.items.filter((o) => selected.includes(o.key)).length}/{group.items.length}
              </span>
            </div>
          )}
          <div className={styles.optionsGrid}>
            {group.items.map((option) => {
              const active = selected.includes(option.key);
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onToggle(option.key)}
                  aria-pressed={active}
                  data-testid={`perm-${option.key}`}
                  className={`${styles.optionRow} ${active ? styles.optionRowActive : ''}`}
                >
                  <span className={`${styles.optionCheck} ${active ? styles.optionCheckActive : ''}`}>
                    {active ? '✓' : ''}
                  </span>
                  <div className={styles.optionInfo}>
                    <span className={styles.optionTitle}>{option.label}</span>
                    <span className={styles.optionDesc}>{option.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}

function MyPermissionsPanel({ admin }: { admin: AdminSummary }) {
  const isDefault = !admin.custom_role_id;
  const pages = admin.allowed_pages ?? [];
  const features = admin.allowed_features ?? [];
  const actions = admin.allowed_actions ?? [];

  return (
    <div className={styles.myPermsCard}>
      <div className={styles.myPermsHeader}>
        <div>
          <h4 className={styles.myPermsTitle}>My permissions</h4>
          <p className={styles.myPermsSubtitle}>
            {isDefault
              ? 'You have full default Admin access — all pages and actions are available.'
              : `Assigned role: ${admin.role_name ?? 'Custom role'}`}
          </p>
        </div>
        <span className={isDefault ? styles.myPermsBadgeDefault : styles.myPermsBadgeCustom}>
          {isDefault ? 'Full access' : 'Custom role'}
        </span>
      </div>

      {!isDefault && (
        <div className={styles.myPermsBody}>
          <div className={styles.myPermsGroup}>
            <span className={styles.myPermsLabel}>Pages</span>
            <div className={styles.chipRow}>
              {pages.length > 0
                ? pages.map((p) => <span key={p} className={styles.chip}>{p.replace(/_/g, ' ')}</span>)
                : <span className={styles.myPermsNone}>None</span>}
            </div>
          </div>
          <div className={styles.myPermsGroup}>
            <span className={styles.myPermsLabel}>Features</span>
            <div className={styles.chipRow}>
              {features.length > 0
                ? features.map((f) => <span key={f} className={styles.chip}>{f.replace(/_/g, ' ')}</span>)
                : <span className={styles.myPermsNone}>None</span>}
            </div>
          </div>
          <div className={styles.myPermsGroup}>
            <span className={styles.myPermsLabel}>Actions</span>
            <div className={styles.chipRow}>
              {actions.length > 0
                ? actions.map((a) => <span key={a} className={styles.chip}>{a.replace(/_/g, ' ')}</span>)
                : <span className={styles.myPermsNone}>None</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CustomRolesPanel({
  currentAdmin,
  canManage = true,
}: {
  currentAdmin?: AdminSummary | null;
  canManage?: boolean;
}) {
  const [roles, setRoles] = useState<AdminCustomRole[]>([]);
  const [catalogue, setCatalogue] = useState<RoleCatalogue>(EMPTY_CATALOGUE);
  // Its own slot: the roles list loading successfully used to clear this,
  // leaving an empty permission list with nothing to explain why.
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<RoleDraft>(createEmptyDraft);

  const loadRoles = async () => {
    const token = window.localStorage.getItem('policyora_token');
    if (!token) {
      setError('Authentication required to manage custom roles.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getAdminCustomRoles(token);
      setRoles(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to load custom roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRoles();
  }, []);

  useEffect(() => {
    // Fetched rather than hardcoded so the editor offers exactly what the API
    // will accept. A failure leaves the groups empty, which shows nothing to
    // tick — better than offering permissions that would be dropped on save.
    const token = window.localStorage.getItem('policyora_token');
    if (!token) return;
    getRoleCatalogue(token)
      .then(setCatalogue)
      .catch((err) => setCatalogueError(
        err instanceof Error ? err.message : 'Could not load the permission list'));
  }, []);

  const toggleSelection = (bucket: PermissionBucket, key: string) => {
    setDraft((prev) => ({
      ...prev,
      [bucket]: prev[bucket].includes(key)
        ? prev[bucket].filter((item) => item !== key)
        : [...prev[bucket], key],
    }));
    setSavedMessage(null);
    setError(null);
  };

  const resetDraft = () => {
    setDraft(createEmptyDraft());
    setEditingId(null);
    setSavedMessage(null);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const token = window.localStorage.getItem('policyora_token');
    const name = draft.name.trim();
    if (!token) {
      setError('Authentication required to manage custom roles.');
      return;
    }
    if (!name) return;

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        description: draft.description.trim(),
        pages: draft.pages,
        features: draft.features,
        actions: draft.actions,
      };

      if (editingId !== null) {
        const updated = await updateAdminCustomRole(token, editingId, payload);
        setRoles((prev) => prev.map((role) => (role.id === editingId ? updated : role)));
        setSavedMessage('Role updated successfully.');
      } else {
        const created = await createAdminCustomRole(token, payload);
        setRoles((prev) => [created, ...prev]);
        setSavedMessage('Custom role created successfully.');
      }

      setDraft(createEmptyDraft());
      setEditingId(null);
    } catch (err) {
      setError((err as Error).message || 'Failed to save custom role');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (role: AdminCustomRole) => {
    setDraft({
      name: role.name,
      description: role.description || '',
      pages: [...role.pages],
      features: [...role.features],
      actions: [...role.actions],
    });
    setEditingId(role.id);
    setSavedMessage(null);
    setError(null);
  };

  const handleDelete = async (roleId: number) => {
    const token = window.localStorage.getItem('policyora_token');
    if (!token) {
      setError('Authentication required to manage custom roles.');
      return;
    }
    if (!window.confirm('Delete this custom role?')) return;

    setDeletingId(roleId);
    setError(null);
    try {
      await deleteAdminCustomRole(token, roleId);
      setRoles((prev) => prev.filter((role) => role.id !== roleId));
      if (editingId === roleId) {
        resetDraft();
      }
      setSavedMessage('Role removed.');
    } catch (err) {
      setError((err as Error).message || 'Failed to delete custom role');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* Page header — replaces the old .hero section */}
      <div className={styles.pageHeader}>
        <div>
          <h3 className={styles.pageTitle}>Custom Roles</h3>
          <p className={styles.pageSubtitle}>Create permission templates and assign them when adding admins.</p>
        </div>
        <div className={styles.headerBadges}>
          <span className={styles.badge}>{roles.length} saved</span>
        </div>
      </div>

      {currentAdmin && <MyPermissionsPanel admin={currentAdmin} />}

      {!canManage && (
        <div className={styles.readOnlyNotice}>
          You have read-only access to custom roles.
        </div>
      )}

      <div className={styles.helperNotice}>
        <strong>Tip:</strong> saved roles can be assigned from <strong>Admin Management → Add New Admin</strong>. Leave the role empty to keep default <strong>Admin</strong> access.
      </div>

      <div className={styles.grid}>
        {/* Form panel — only rendered when canManage is true */}
        {canManage && (
          <form className={styles.formPanel} onSubmit={handleSubmit}>
            <div className={styles.formHeader}>
              <div>
                <h4 className={styles.panelTitle}>{editingId ? 'Edit role' : 'Create a custom role'}</h4>
                <p className={styles.panelText}>Pick the exact pages and features this role should be allowed to open.</p>
              </div>
              <span className={styles.summaryPill}>
                {draft.pages.length + draft.features.length + draft.actions.length} permissions
              </span>
            </div>

            <label className={styles.fieldLabel}>
              Role name
              <input
                className={styles.textInput}
                type="text"
                value={draft.name}
                onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Compliance Reviewer"
              />
            </label>

            <label className={styles.fieldLabel}>
              Description
              <textarea
                className={styles.textArea}
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Briefly describe when this role should be used"
              />
            </label>

            {catalogueError && (
              <div className={styles.errorNotice} data-testid="catalogue-error">
                <AlertTriangle size={16} aria-hidden /> {catalogueError}
              </div>
            )}

            <PermissionGroup
              title="Admin pages"
              groups={catalogue.pages}
              selected={draft.pages}
              onToggle={(key) => toggleSelection('pages', key)}
            />

            <PermissionGroup
              title="Product features and GRC"
              groups={catalogue.features}
              selected={draft.features}
              onToggle={(key) => toggleSelection('features', key)}
            />

            <PermissionGroup
              title="Sensitive actions"
              groups={catalogue.actions}
              selected={draft.actions}
              onToggle={(key) => toggleSelection('actions', key)}
            />

            <div className={styles.actionRow}>
              <button className={styles.primaryButton} type="submit" disabled={saving || !draft.name.trim()}>
                {saving ? 'Saving…' : editingId ? 'Update role' : 'Create role'}
              </button>
              <button className={styles.secondaryButton} type="button" onClick={resetDraft}>
                Reset
              </button>
            </div>

            {savedMessage && <div className={styles.savedNotice}>✓ {savedMessage}</div>}
            {error && <div className={styles.errorNotice}><AlertTriangle size={16} aria-hidden /> {error}</div>}
          </form>
        )}

        <aside className={styles.listPanel}>
          <div className={styles.listHeader}>
            <h4 className={styles.panelTitle}>Saved roles</h4>
            <p className={styles.panelText}>Review, edit, or remove the permission templates you have already created.</p>
          </div>

          {/* Show error in list panel when canManage is false (form panel hidden) */}
          {!canManage && error && <div className={styles.errorNotice}><AlertTriangle size={16} aria-hidden /> {error}</div>}

          {loading ? (
            <div className={styles.emptyState}>
              <strong>Loading roles…</strong>
              <span>Please wait while saved permissions are fetched.</span>
            </div>
          ) : roles.length === 0 ? (
            <div className={styles.emptyState}>
              <strong>No custom roles yet.</strong>
              <span>Create your first role to start assigning page and feature access.</span>
            </div>
          ) : (
            <div className={styles.roleList}>
              {roles.map((role) => (
                <article key={role.id} className={styles.roleCard}>
                  <div className={styles.roleCardTop}>
                    <div className={styles.roleCardMeta}>
                      <h5 className={styles.roleName}>{role.name}</h5>
                      {role.description && <p className={styles.roleDescription}>{role.description}</p>}
                    </div>
                    <span className={styles.roleDate}>{new Date(role.created_at).toLocaleDateString()}</span>
                  </div>

                  <div className={styles.roleMetaRow}>
                    <span className={styles.roleMetaPill}>{role.pages.length} pages</span>
                    <span className={styles.roleMetaPill}>{role.features.length} features</span>
                    <span className={styles.roleMetaPill}>{role.actions.length} actions</span>
                  </div>

                  <div className={styles.rolePermGrid}>
                    {role.pages.length > 0 && (
                      <div className={styles.rolePermCol}>
                        <span className={`${styles.sectionLabel} ${styles.labelPages}`}>Pages</span>
                        <div className={styles.chipRow}>
                          {role.pages.map((item) => (
                            <span key={`${role.id}-p-${item}`} className={`${styles.chip} ${styles.chipPage}`}>{item.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {role.features.length > 0 && (
                      <div className={styles.rolePermCol}>
                        <span className={`${styles.sectionLabel} ${styles.labelFeatures}`}>Features</span>
                        <div className={styles.chipRow}>
                          {role.features.map((item) => (
                            <span key={`${role.id}-f-${item}`} className={`${styles.chip} ${styles.chipFeature}`}>{item.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {role.actions.length > 0 && (
                      <div className={styles.rolePermCol}>
                        <span className={`${styles.sectionLabel} ${styles.labelActions}`}>Actions</span>
                        <div className={styles.chipRow}>
                          {role.actions.map((item) => (
                            <span key={`${role.id}-a-${item}`} className={`${styles.chip} ${styles.chipAction}`}>{item.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <div className={styles.cardActions}>
                      <button type="button" className={styles.cardButton} onClick={() => handleEdit(role)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`${styles.cardButton} ${styles.deleteButton}`}
                        onClick={() => handleDelete(role.id)}
                        disabled={deletingId === role.id}
                      >
                        {deletingId === role.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
